import { RuleSection } from '../types';

export const baseSections: RuleSection[] = [
  {
    "id": "edition-profile",
    "title": "Profil edycji C3 / C3.1",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "corpus-base",
      "polish-base-c3"
    ],
    "keywords": [
      "edycja",
      "C3",
      "C3.1"
    ],
    "level": "basic",
    "hubGroup": "Wprowadzenie",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Carcassonne ma kilka szat graficznych i linii rozszerzeń. Ten rozdział mówi, które zasady obowiązują w aplikacji: podstawa C3, Rzeka i Opat C3, duże dodatki C3.1 z 2025 oraz minidodatki Big Box C3. Dzięki temu nie przenosisz pamięci ze starszych wydań tylko dlatego, że tytuł brzmi podobnie."
      },
      {
        "type": "heading",
        "text": "Jakie zasady obowiązują",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "C3 to trzecia szata graficzna podstawy (od 2020/2021). C3.1 to zrewidowana linia głównych rozszerzeń od 2025: te same duże dodatki mają nowe procedury, inne figury i inne momenty aktywacji niż starsze odpowiedniki. Minidodatki Big Box C3 pozostają osobnymi, małymi modułami i nie zastępują majorów C3.1."
      },
      {
        "type": "image",
        "imageId": "edition-warning",
        "alt": "Ostrzeżenie przed mieszaniem C3.1 z wcześniejszymi zasadami.",
        "caption": "Profil edycji"
      },
      {
        "type": "editionWarning",
        "text": "Nie importuj mechanik C1/C2 tylko dlatego, że stary dodatek miał podobny tytuł.",
        "oldEditionName": "Księżniczka i Smok / Wieża / Opactwo i Burmistrz / Hrabia Król i Rzeka / Katapulta / Mosty Zamki i Bazary / Wzgórza i Owce / Cyrk Objazdowy"
      },
      {
        "type": "bulletList",
        "items": [
          "C3 — trzecia szata graficzna od 2020/2021.",
          "C3.1 — zrewidowana linia głównych rozszerzeń od 2025.",
          "Rzeka i Opat — elementy C3 domyślnie włączone w konfiguracji.",
          "Mgła nad Carcassonne — osobny tryb; domyślnie wyłączona."
        ]
      },
      {
        "type": "commonMistake",
        "text": "Stosowanie reguł starej Wieży (porywanie meepli) przy module Wieże i Złodzieje C3.1. Korekta: w C3.1 wieże używają strażników i rozliczenia złodzieja, bez porywania."
      },
      {
        "type": "crossReference",
        "sectionId": "goal",
        "label": "Następnie: cel gry i intuicja krajobrazu"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Aplikacja uczy profilu C3 / C3.1, nie mieszanki pamięci z C1/C2.",
          "Podobny tytuł dodatku nie oznacza tych samych zasad.",
          "Włączaj tylko moduły, które macie na stole."
        ]
      },
      {
        "type": "qa",
        "question": "Czy mogę grać C3.1 według pamięci z Księżniczki i Smoka?",
        "answer": "Nie. Wróżka i Smok C3.1 mają własne procedury (m.in. dwa proste ruchy smoka). Stare reguły z Księżniczki i Smoka nie obowiązują w tym module."
      }
    ],
    "imageIds": [
      "edition-warning"
    ]
  },
  {
    "id": "goal",
    "title": "Cel gry: budowanie krajobrazu i zdobywanie punktów",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3",
      "corpus-base"
    ],
    "keywords": [
      "cel",
      "punkty"
    ],
    "level": "basic",
    "hubGroup": "Wprowadzenie",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Carcassonne jest grą o wspólnym budowaniu mapy i własnym wyborze, które jej fragmenty warto przejąć. W tym rozdziale poznasz cel partii, znaczenie punktów oraz prostą zależność między kafelkiem, figurą i ukończonym obszarem. To ważny punkt wyjścia: każda późniejsza zasada — od dokładania kafelka po punktowanie pola — służy temu samemu celowi, czyli zdobyciu jak największej liczby punktów."
      },
      {
        "type": "heading",
        "text": "Na czym polega gra",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Wszyscy gracze dokładają po jednym kafelku do wspólnego krajobrazu. Kafelki tworzą drogi, miasta, klasztory i pola. Po dołożeniu kafelka możesz wystawić swoją figurę na obszarze tego właśnie kafelka, aby wskazać, że chcesz czerpać punkty z tego obszaru. Figura nie zajmuje kafelka na własność; zajmuje konkretną drogę, miasto, klasztor albo pole, które może później rozrosnąć się przez kolejne kafelki."
      },
      {
        "type": "paragraph",
        "text": "Najczęściej punkty pojawiają się wtedy, gdy droga, miasto albo klasztor zostaną ukończone. Figura z takiego ukończonego obszaru wraca do twojego zasobu, więc można użyć jej ponownie. Pola działają inaczej: chłop pozostaje na nich zwykle do końca partii, a punkty za pole liczy się dopiero podczas punktowania końcowego. Ta różnica tworzy podstawową decyzję gry: czy użyć figury teraz na szybki, mały zysk, czy związać ją na dłużej z obszarem, który może przynieść więcej punktów."
      },
      {
        "type": "heading",
        "text": "Jak wygrywasz",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Partia kończy się po rozegraniu pełnej tury z ostatnim kafelkiem. Następnie gracze wykonują punktowanie końcowe, także za nieukończone obszary i pola. Wygrywa gracz z największą liczbą punktów na torze punktacji. Nie wygrywa osoba z największą liczbą figurek na planszy ani osoba, która ukończyła najwięcej miast; liczy się wyłącznie łączny wynik."
      },
      {
        "type": "table",
        "headers": [
          "Element decyzji",
          "Co oznacza w praktyce",
          "Kiedy przynosi punkty"
        ],
        "rows": [
          [
            "Kafelek",
            "Rozbudowuje wspólną mapę i może połączyć albo zamknąć obszar.",
            "Sam kafelek nie daje punktów; może ukończyć obszar."
          ],
          [
            "Figura na drodze, w mieście lub klasztorze",
            "Pozwala uczestniczyć w punktowaniu tego obszaru.",
            "Zwykle po ukończeniu obszaru w trakcie gry."
          ],
          [
            "Chłop na polu",
            "Pozwala uczestniczyć w punktowaniu pola.",
            "Wyłącznie podczas punktowania końcowego."
          ],
          [
            "Tor punktacji",
            "Pokazuje bieżący, łączny wynik każdego gracza.",
            "Za każdym razem, gdy zasada przyznaje punkty."
          ]
        ]
      },
      {
        "type": "heading",
        "text": "Pełny przykład: wybór celu dla jednej figury",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Anna i Bartek rozgrywają partię. Anna ma figurę na drodze, która jest już prawie ukończona. Bartek właśnie dobrał kafelek, którym może zamknąć tę drogę. Nie ma znaczenia, że to Bartek dokłada kafelek: po zamknięciu obszaru sprawdza się, czyj meeple znajduje się na drodze, a nie kto wykonał ostatni ruch."
      },
      {
        "type": "table",
        "headers": [
          "Stan przed ruchem",
          "Decyzja Bartka",
          "Kroki rozstrzygnięcia",
          "Wynik",
          "Dlaczego jest poprawny"
        ],
        "rows": [
          [
            "Anna ma podróżnika na drodze; droga ma jedno otwarte zakończenie. Bartek nie ma figury na tej drodze.",
            "Bartek dokłada legalny kafelek, który zamyka ostatnie zakończenie drogi, i nie wystawia figury.",
            "1. Kafelek zostaje dołożony. 2. Droga jest ukończona. 3. Punktuje gracz kontrolujący drogę. 4. Figura Anny wraca do jej zasobu.",
            "Anna dostaje punkty za ukończoną drogę i odzyskuje figurę; Bartek nie dostaje punktów za tę drogę.",
            "Punkty przypadają kontrolującemu obszar, nie osobie dokładającej ostatni kafelek. Ukończona droga zwalnia figurę do dalszego użycia."
          ]
        ]
      },
      {
        "type": "heading",
        "text": "Najczęstsze błędne założenia",
        "level": 2
      },
      {
        "type": "commonMistake",
        "text": "„Dokładam ostatni kafelek, więc punkty są moje.” Korekta: punktuje gracz albo gracze mający największą siłę figurek na ukończonym obszarze. Osoba, która zamknęła obszar, może być zupełnie inna."
      },
      {
        "type": "commonMistake",
        "text": "„Każda figura wraca zaraz po ruchu.” Korekta: wraca tylko figura z obszaru, który właśnie zapunktował i kończy swoje rozpatrywanie. Chłop na polu zwykle czeka do końca gry."
      },
      {
        "type": "commonMistake",
        "text": "„Wystawienie figury zawsze jest korzystne.” Korekta: figura pozostaje zajęta, dopóki jej obszar nie zapunktuje. Czasem lepiej zachować ją na kolejny kafelek."
      },
      {
        "type": "heading",
        "text": "Granice tej zasady i połączenia z kolejnymi rozdziałami",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Podstawa opisuje cztery rodzaje obszarów. Aktywne rozszerzenia mogą dodać własne źródła punktów, figury i wyjątki, lecz nie zmieniają podstawowej zasady zwycięstwa: porównuje się końcowy wynik wszystkich graczy. Gdy kilka figurek znajdzie się później na jednym obszarze, o punktach decyduje większość — omówiona w rozdziale „Większość i remisy”."
      },
      {
        "type": "crossReference",
        "sectionId": "components",
        "label": "Następnie: rozpoznaj kafelki, figury i tor punktacji"
      },
      {
        "type": "crossReference",
        "sectionId": "turn-structure",
        "label": "Zobacz pełną kolejność tury"
      },
      {
        "type": "crossReference",
        "sectionId": "roads",
        "label": "Dowiedz się, jak punktują drogi"
      },
      {
        "type": "crossReference",
        "sectionId": "cities",
        "label": "Dowiedz się, jak punktują miasta"
      },
      {
        "type": "crossReference",
        "sectionId": "fields",
        "label": "Dowiedz się, dlaczego pola punktują na końcu"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Wspólnie budujecie jedną mapę, ale każdy zdobywa własne punkty.",
          "Figura pozwala uczestniczyć w punktowaniu obszaru, na którym stoi.",
          "Drogi, miasta i klasztory zwykle punktują po ukończeniu; pola wyłącznie na końcu gry.",
          "Po punktowaniu końcowym wygrywa najwyższy łączny wynik."
        ]
      },
      {
        "type": "heading",
        "text": "Pytania na start",
        "level": 2
      },
      {
        "type": "qa",
        "question": "Czy zdobywam punkty za każdy kafelek, który dołożę?",
        "answer": "Nie. Kafelek może doprowadzić do ukończenia obszaru, ale punkty za ten obszar dostaje gracz kontrolujący go figurą zgodnie z zasadą większości."
      },
      {
        "type": "qa",
        "question": "Czy muszę wystawiać figurę po każdym kafelku?",
        "answer": "Nie. Wystawienie figury jest opcjonalne. Możesz ją zachować, gdy dostępny obszar jest niepewny albo gdy chcesz użyć figury w późniejszej turze."
      },
      {
        "type": "qa",
        "question": "Czy mogę wygrać, jeśli mam mniej ukończonych miast niż inni?",
        "answer": "Tak. Zwycięstwo zależy od sumy wszystkich punktów, a nie od liczby ukończonych obszarów jednego rodzaju."
      }
    ]
  },
  {
    "id": "components",
    "title": "Elementy gry: co leży na stole i do czego służy",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3"
    ],
    "keywords": [
      "kafelki",
      "meeple"
    ],
    "level": "basic",
    "hubGroup": "Wprowadzenie",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Przed pierwszą partią warto rozpoznać cztery typy elementów krajobrazu i odróżnić figurę wystawianą na mapę od znacznika wyniku. Dzięki temu przygotowanie stołu będzie bezbłędne, a podczas tury od razu zobaczysz, gdzie wolno położyć kafelek i co oznacza pozycja figury. Ten rozdział prowadzi bezpośrednio do przygotowania gry, a potem do zasad dokładania kafelków i wystawiania meepli."
      },
      {
        "type": "image",
        "imageId": "features-overview",
        "alt": "Droga, miasto, klasztor i pole.",
        "caption": "Elementy krajobrazu"
      },
      {
        "type": "heading",
        "text": "Kafelki krajobrazu",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Kafelki to kwadratowe fragmenty wspólnej mapy. Każdy pokazuje jeden lub kilka rodzajów terenu: miasto, drogę, pole, a czasem klasztor. Po bokach kafelka widać, jaki teren dochodzi do jego krawędzi. Podczas gry obracasz kafelek i dokładasz go tak, aby każdy bok stykający się z innym kafelkiem pokazywał zgodny teren. Kafelki nie należą do konkretnego gracza — z tej samej puli dobierają je wszyscy."
      },
      {
        "type": "paragraph",
        "text": "Miasto rozpoznasz po zabudowie i murze, drogę po pasie prowadzącym przez teren, pole po otwartej zielonej przestrzeni, a klasztor po budynku umieszczonym w środku kafelka. Jeden kafelek może zawierać kilka z tych obszarów. To istotne przy wystawianiu figury: wybierasz konkretny obszar na kafelku, a nie po prostu „ten kafelek”."
      },
      {
        "type": "heading",
        "text": "Figury graczy i tor punktacji",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Każdy gracz wybiera jeden kolor i otrzymuje figury tego koloru. Jedna z nich rozpoczyna grę na polu 0 toru punktacji. Jest wyłącznie znacznikiem wyniku: przesuwasz ją za każdym razem, gdy zdobywasz punkty, ale nie wolno wystawiać jej na kafelek. Pozostałe figury tworzą osobisty zasób. Z niego bierzesz figurę, gdy po dołożeniu kafelka chcesz zająć obszar."
      },
      {
        "type": "paragraph",
        "text": "Ta sama zwykła figura otrzymuje nazwę zależną od miejsca, na którym stoi: na drodze jest podróżnikiem, w mieście rycerzem, w klasztorze mnichem, a na polu chłopem. Nie są to cztery różne pionki ani cztery różne zdolności. Pozycja figury określa jedynie, jaki obszar kontroluje i według jakiej reguły będzie punktować."
      },
      {
        "type": "table",
        "headers": [
          "Element",
          "Jak go rozpoznać",
          "Do czego służy",
          "Kiedy go używasz"
        ],
        "rows": [
          [
            "Kafelek krajobrazu",
            "Kwadrat z drogą, miastem, polem i czasem klasztorem.",
            "Powiększa wspólną mapę.",
            "Na początku swojej tury dobierasz i dokładasz jeden."
          ],
          [
            "Zwykła figura z zasobu",
            "Figura w wybranym przez gracza kolorze, poza torem punktacji.",
            "Zajmuje wybrany obszar i umożliwia udział w jego punktowaniu.",
            "Opcjonalnie po dołożeniu kafelka."
          ],
          [
            "Znacznik punktacji",
            "Figura tego samego koloru ustawiona na torze punktacji.",
            "Pokazuje łączny wynik gracza.",
            "Przesuwasz go, gdy otrzymujesz punkty."
          ],
          [
            "Tor punktacji",
            "Numerowany tor obok krajobrazu.",
            "Zapamiętuje bieżące wyniki wszystkich graczy.",
            "Przez całą partię."
          ]
        ]
      },
      {
        "type": "heading",
        "text": "Pełny przykład: ten sam pionek, inne znaczenie",
        "level": 2
      },
      {
        "type": "table",
        "headers": [
          "Stan",
          "Decyzja",
          "Kroki",
          "Wynik",
          "Dlaczego jest poprawny"
        ],
        "rows": [
          [
            "Celina ma w zasobie zwykłą figurę. Jej kafelek zawiera drogę i pole.",
            "Celina kładzie figurę na drodze, nie na polu.",
            "1. Dokłada kafelek legalnie. 2. Wybiera drogę na właśnie dołożonym kafelku. 3. Ustawia na niej jedną figurę ze swojego zasobu.",
            "Figura Celiny jest podróżnikiem i może punktować tę drogę po jej ukończeniu.",
            "Nazwa roli wynika z obszaru, na którym stoi figura. Ten sam pionek położony na polu byłby chłopem i podlegałby zasadzie pól."
          ]
        ]
      },
      {
        "type": "heading",
        "text": "Najczęstsze pomyłki przy rozkładaniu elementów",
        "level": 2
      },
      {
        "type": "commonMistake",
        "text": "Położenie wszystkich figurek danego koloru obok toru punktacji. Korekta: tylko jedna figura jest znacznikiem wyniku; pozostałe muszą być łatwo dostępne jako zasób do wystawiania."
      },
      {
        "type": "commonMistake",
        "text": "Traktowanie całego kafelka jako jednego obszaru. Korekta: droga, miasto i pole na tym samym kafelku są odrębnymi obszarami; przy wystawieniu wskazujesz jeden z nich."
      },
      {
        "type": "commonMistake",
        "text": "Zdejmowanie znacznika z toru punktacji, aby użyć go na mapie. Korekta: znacznik wyniku pozostaje na torze przez całą partię."
      },
      {
        "type": "heading",
        "text": "Wyjątki i połączenia z dalszymi zasadami",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Ten rozdział opisuje elementy gry podstawowej. Włączone rozszerzenia mogą dodać oznaczone kafelki, dodatkowe figury lub znaczniki. Ich elementy przygotowuje się tylko wtedy, gdy odpowiedni moduł jest aktywny; nie mieszaj ich z podstawą przypadkowo. Zasady rozpoznawania krawędzi są wspólne, ale szczególne elementy rozszerzeń mają własne rozdziały."
      },
      {
        "type": "crossReference",
        "sectionId": "setup-base",
        "label": "Przejdź do przygotowania gry krok po kroku"
      },
      {
        "type": "crossReference",
        "sectionId": "tile-legality",
        "label": "Sprawdź, jak dopasowywać krawędzie kafelków"
      },
      {
        "type": "crossReference",
        "sectionId": "meeple-placement",
        "label": "Sprawdź, gdzie wolno wystawić figurę"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Kafelki tworzą wspólny krajobraz; każdy z nich może zawierać kilka oddzielnych obszarów.",
          "Jedna figura każdego gracza prowadzi wynik na torze punktacji.",
          "Pozostałe figury są zasobem i po położeniu przyjmują rolę wynikającą z obszaru.",
          "Elementy rozszerzeń dodawaj wyłącznie przy aktywnym odpowiednim module."
        ]
      },
      {
        "type": "heading",
        "text": "Pytania na start",
        "level": 2
      },
      {
        "type": "qa",
        "question": "Czy podróżnik, rycerz, mnich i chłop to różne pionki?",
        "answer": "Nie. To nazwy tej samej zwykłej figury użyte zależnie od miejsca, które zajmuje na kafelku."
      },
      {
        "type": "qa",
        "question": "Czy mogę użyć figury z toru punktacji?",
        "answer": "Nie. Ta figura pozostaje znacznikiem wyniku. Wystawiać można wyłącznie figury z osobistego zasobu."
      },
      {
        "type": "qa",
        "question": "Czy kafelek z drogą i miastem pozwala wybrać oba obszary naraz?",
        "answer": "Nie. W podstawowej turze możesz opcjonalnie wystawić najwyżej jedną figurę i wybierasz dla niej jeden konkretny obszar na właśnie dołożonym kafelku."
      },
      {
        "type": "note",
        "text": "Liczba kafelków i figurek zależy od posiadanego wydania oraz aktywnych modułów. Nie zastępuj elementów podstawy podobnymi elementami ze starszych edycji."
      }
    ],
    "imageIds": [
      "features-overview"
    ]
  },
  {
    "id": "setup-base",
    "title": "Przygotowanie gry podstawowej",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3"
    ],
    "keywords": [
      "przygotowanie",
      "setup"
    ],
    "level": "basic",
    "hubGroup": "Przygotowanie",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Dobre przygotowanie sprawia, że później każda tura ma ten sam prosty rytm: dobierasz kafelek, dokładasz go, opcjonalnie wystawiasz figurę, a następnie rozpatrujesz punkty. Tutaj ułożysz elementy tak, aby każdy gracz widział swoje figury, wspólna pula kafelków była losowa, a wynik można było stale śledzić. Po przygotowaniu przejdź od razu do „Przebiegu tury”."
      },
      {
        "type": "heading",
        "text": "Co przygotować przed rozpoczęciem",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Użyj kafelków gry podstawowej oraz elementów tych rozszerzeń, które są aktywne w konfiguracji partii. Nie dodawaj kafelków ani figurek dodatku tylko dlatego, że leżą w tym samym pudełku. Wszystkie używane kafelki powinny tworzyć jedną zakrytą, dobrze potasowaną pulę, z której każdy będzie dobierał losowo."
      },
      {
        "type": "numberedList",
        "items": [
          "Wybierz kolor dla każdego gracza. Każdy bierze swoje figury; jedną ustawia na polu 0 toru punktacji, a resztę kładzie przed sobą jako zasób.",
          "Jeżeli grasz samą podstawą, połóż kafelek startowy na środku wolnego stołu. Zostaw wokół niego dużo miejsca: krajobraz rośnie we wszystkich kierunkach.",
          "Zbierz wszystkie pozostałe używane kafelki, odwróć je obrazkiem do dołu i dokładnie potasuj. Ułóż je w kilku łatwo dostępnych stosach albo w jednym zakrytym zapasie.",
          "Przygotuj elementy aktywnych rozszerzeń zgodnie z ich własnymi rozdziałami. Rzeka zmienia początek krajobrazu, więc przy aktywnej Rzece nie zaczynaj od zwykłego kafelka startowego.",
          "Wybierz pierwszego gracza w dowolny uzgodniony sposób. Następnie gra toczy się kolejno zgodnie z ruchem wskazówek zegara."
        ]
      },
      {
        "type": "heading",
        "text": "Dlaczego każdy krok jest potrzebny",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Kafelek startowy daje wszystkim wspólny początek i zapobiega sytuacji, w której pierwszy gracz wybiera odległe miejsce na pustym stole. Zakryte kafelki wprowadzają losowość, ale nie ukrywają zasad: po dobraniu kafelka wolno go obracać, zanim znajdziesz dla niego legalne miejsce. Osobny zasób figurek przypomina, że figura zajęta na mapie nie jest dostępna do kolejnego wystawienia, a znacznik na torze nie jest częścią tego zasobu."
      },
      {
        "type": "heading",
        "text": "Pełny przykład: przygotowanie partii Anny, Bartka i Celiny",
        "level": 2
      },
      {
        "type": "table",
        "headers": [
          "Stan",
          "Decyzja",
          "Kroki",
          "Wynik",
          "Dlaczego jest poprawny"
        ],
        "rows": [
          [
            "Anna, Bartek i Celina grają podstawą bez dodatkowych modułów. Stół jest pusty.",
            "Przygotowują wspólną pulę i osobiste zasoby przed wyborem pierwszego gracza.",
            "1. Każde wybiera kolor. 2. Każde kładzie jedną figurę na 0 toru. 3. Pozostałe figury odkłada przed sobą. 4. Kładą kafelek startowy na środku. 5. Tasują pozostałe kafelki obrazkiem do dołu. 6. Wybierają Annę na pierwszego gracza.",
            "Anna może zacząć pierwszą turę, a wszyscy widzą wynik 0 i dostępne figury.",
            "Krajobraz ma wspólny początek, dobieranie będzie losowe, a role znacznika wyniku i figur z zasobu są rozdzielone."
          ]
        ]
      },
      {
        "type": "heading",
        "text": "Najczęstsze błędy",
        "level": 2
      },
      {
        "type": "commonMistake",
        "text": "Położenie kafelków obrazkiem do góry przed grą. Korekta: kafelki dobiera się losowo z zakrytej puli; nie wybiera się następnego kafelka spośród widocznych."
      },
      {
        "type": "commonMistake",
        "text": "Rozpoczęcie od kafelka startowego i jednoczesne rozpoczęcie Rzeki. Korekta: Rzeka zastępuje zwykły start krajobrazu; użyj jej rozdziału przygotowania, gdy moduł Rzeka jest aktywny."
      },
      {
        "type": "commonMistake",
        "text": "Ustawienie wszystkich figurek na torze punktacji. Korekta: na torze stoi jedna figura każdego gracza; pozostałe muszą tworzyć jego dostępny zasób."
      },
      {
        "type": "heading",
        "text": "Wyjątki i dalsza droga",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Aktywne rozszerzenia mogą zmienić przygotowanie, lecz nie podstawową kolejność graczy ani sposób prowadzenia wyniku. Rzeka zmienia pierwszy układ kafelków, a Opat dodaje własną figurę i zasady jej użycia. Zawsze najpierw przygotuj podstawę, następnie nałóż tylko instrukcje aktywnych modułów."
      },
      {
        "type": "crossReference",
        "sectionId": "river-setup",
        "label": "Jeśli Rzeka jest aktywna: przygotowanie Rzeki"
      },
      {
        "type": "crossReference",
        "sectionId": "abbot-rules",
        "label": "Jeśli Opat jest aktywny: poznaj jego figurę"
      },
      {
        "type": "crossReference",
        "sectionId": "turn-structure",
        "label": "Następnie: rozegraj pierwszą turę"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Każdy gracz ma jedną figurę na polu 0 toru, a pozostałe przed sobą.",
          "Przy podstawie kafelek startowy leży na środku stołu.",
          "Pozostałe używane kafelki tasuje się zakryte.",
          "Rzeka zastępuje zwykły start; dodatki przygotowuj tylko, gdy są aktywne."
        ]
      },
      {
        "type": "heading",
        "text": "Pytania na start",
        "level": 2
      },
      {
        "type": "qa",
        "question": "Czy pierwszy gracz dostaje dodatkową figurę albo punkty?",
        "answer": "Nie. Pierwszy gracz jedynie wykonuje pierwszą turę; wszyscy zaczynają z wynikiem 0 i przygotowanym własnym zasobem figurek."
      },
      {
        "type": "qa",
        "question": "Czy mogę oglądać kafelki podczas tasowania?",
        "answer": "Nie. Po potasowaniu kafelki pozostają zakryte aż do dobrania przez gracza w jego turze."
      },
      {
        "type": "qa",
        "question": "Czy dodatki zawsze muszą być użyte, gdy mam je w pudełku?",
        "answer": "Nie. Używaj wyłącznie aktywnych modułów i ich elementów; pozostałe zostają poza pulą tej partii."
      }
    ]
  },
  {
    "id": "turn-structure",
    "title": "Przebieg tury: od dobrania do punktów",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3",
      "wica-order"
    ],
    "keywords": [
      "tura",
      "kolejnosc"
    ],
    "level": "basic",
    "hubGroup": "Tura",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Każda tura Carcassonne ma niezmienną kolejność. Najpierw powiększasz krajobraz kafelkiem, potem podejmujesz decyzję o figurze, a na końcu sprawdzasz, czy nowy kafelek ukończył któryś obszar. Ta kolejność jest ważna, ponieważ nie wolno punktować przed decyzją o figurze ani wystawiać figury po punktowaniu. Opanowanie jej pozwala samodzielnie rozstrzygać większość sytuacji przy stole."
      },
      {
        "type": "image",
        "imageId": "base-turn-order",
        "alt": "Trzy etapy tury.",
        "caption": "Przebieg tury"
      },
      {
        "type": "resolutionOrder",
        "title": "Trzy obowiązkowe etapy tury",
        "steps": [
          "Dobierz jeden zakryty kafelek. Obróć go i dołóż legalnie, tak aby przynajmniej jednym bokiem stykał się z krajobrazem. Jeżeli nie ma żadnego legalnego miejsca, odłóż kafelek na bok i dobierz nowy.",
          "Po dołożeniu kafelka możesz nie wystawiać nikogo albo umieścić najwyżej jedną figurę ze swojego zasobu na właśnie dołożonym kafelku. Musi to być legalny, niezajęty obszar. Nie możesz później w tej turze zmienić jej miejsca.",
          "Sprawdź wszystkie drogi, miasta i klasztory ukończone przez ten kafelek. Przyznaj punkty graczowi albo graczom z większością, a następnie zwróć ich figury z zapunktowanych obszarów do zasobów."
        ]
      },
      {
        "type": "heading",
        "text": "Dlaczego punktowanie jest ostatnie",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Nowy kafelek może jednocześnie zamknąć kilka obszarów. Po jego dołożeniu widzisz już cały stan krajobrazu i możesz jeszcze zdecydować, czy wystawić figurę na legalnym, niezajętym fragmencie nowego kafelka. Dopiero wtedy rozstrzyga się ukończenia. Jeżeli figura stoi w ukończonym obszarze, jej właściciel otrzymuje punkty i odzyskuje ją przed rozpoczęciem tury następnego gracza."
      },
      {
        "type": "heading",
        "text": "Pełny przykład jednej tury",
        "level": 2
      },
      {
        "type": "table",
        "headers": ["Stan przed ruchem", "Decyzja Anny", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna ma jedną figurę w zasobie. Jej rycerz stoi w mieście z jedną otwartą krawędzią.",
            "Dobiera kafelek pasujący do tej krawędzi i wybiera, by nie wystawiać kolejnej figury.",
            "1. Dokłada kafelek legalnie. 2. Rezygnuje z wystawienia figury. 3. Miasto staje się ukończone. 4. Sprawdza większość. 5. Przesuwa wynik i zwraca rycerza do zasobu.",
            "Anna punktuje ukończone miasto i od razu odzyskuje rycerza na przyszłe tury.",
            "Wystawienie figury jest opcjonalne, lecz ukończenia zawsze sprawdza się po decyzji o figurze."
          ]
        ]
      },
      {
        "type": "heading",
        "text": "Błędy, które psują kolejność",
        "level": 2
      },
      {
        "type": "commonMistake",
        "text": "Punktowanie ukończonego obszaru natychmiast po położeniu kafelka. Korekta: najpierw gracz decyduje o figurze, dopiero potem rozpatruje ukończenia."
      },
      {
        "type": "commonMistake",
        "text": "Wystawienie figury na starym kafelku, ponieważ właśnie wróciła z punktowania. Korekta: zwykłą figurę można wystawić tylko na kafelku dołożonym w tej turze; odzyskana figura jest dostępna dopiero w przyszłej turze."
      },
      {
        "type": "commonMistake",
        "text": "Pomijanie punktowania, bo gracz nie wystawił figury. Korekta: ukończenia sprawdza się po każdym dołożonym kafelku, niezależnie od decyzji o wystawieniu."
      },
      {
        "type": "heading",
        "text": "Wyjątki i dalsze rozdziały",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Rozszerzenia mogą dodać efekty przed, w trakcie albo po tych etapach oraz akcje zamiast wystawienia figury. Nie zmieniają jednak podstawowego porządku, dopóki ich własny rozdział nie mówi wyraźnie inaczej. W szczególności Opat może zostać wycofany zamiast wystawienia figury. Szczegółowe warunki każdego etapu znajdziesz w kolejnych rozdziałach."
      },
      {
        "type": "crossReference",
        "sectionId": "tile-legality",
        "label": "Etap 1: legalne dokładanie kafelka"
      },
      {
        "type": "crossReference",
        "sectionId": "meeple-placement",
        "label": "Etap 2: legalne wystawianie figury"
      },
      {
        "type": "crossReference",
        "sectionId": "mid-scoring",
        "label": "Etap 3: punktowanie w trakcie gry"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Dobierz i legalnie dołóż dokładnie jeden kafelek.",
          "Opcjonalnie wystaw najwyżej jedną figurę na nowym kafelku.",
          "Następnie rozpatrz wszystkie ukończenia wywołane tym kafelkiem.",
          "Figury z zapunktowanych ukończonych obszarów wracają do zasobu."
        ]
      },
      {
        "type": "heading",
        "text": "Pytania na start",
        "level": 2
      },
      {
        "type": "qa",
        "question": "Czy muszę wystawić figurę, gdy mam ją w zasobie?",
        "answer": "Nie. Wystawienie figury jest decyzją opcjonalną po dołożeniu kafelka."
      },
      {
        "type": "qa",
        "question": "Czy mogę wystawić figurę po zapunktowaniu miasta?",
        "answer": "Nie. Etap wystawienia kończy się przed punktowaniem. Odzyskana figura będzie dostępna do wystawienia dopiero w późniejszej turze."
      },
      {
        "type": "qa",
        "question": "Co jeśli jeden kafelek ukończy kilka obszarów?",
        "answer": "Po etapie figury sprawdź i rozpatrz każdy z ukończonych obszarów. Każdy punktuje według własnej zasady oraz większości na tym obszarze."
      }
    ],
    "imageIds": [
      "base-turn-order"
    ]
  },
  {
    "id": "tile-legality",
    "title": "Dokładanie kafelka: kiedy ruch jest legalny",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3"
    ],
    "keywords": [
      "dopasowanie",
      "krawedz"
    ],
    "level": "basic",
    "hubGroup": "Tura",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Pierwszy etap tury nie polega na położeniu kafelka w dowolnym wolnym miejscu. Musi on powiększać istniejący krajobraz i jednocześnie pasować do każdego boku, którego dotyka. Ta zasada utrzymuje czytelne drogi, miasta i pola, a zarazem daje graczom decyzje: ten sam kafelek często da się obrócić i położyć na kilka legalnych sposobów."
      },
      {
        "type": "image",
        "imageId": "base-edge-match",
        "alt": "Zgodne i niezgodne krawędzie.",
        "caption": "Dopasowanie krawędzi"
      },
      {
        "type": "heading",
        "text": "Jak sprawdzić kafelek krok po kroku",
        "level": 2
      },
      {
        "type": "numberedList",
        "items": [
          "Wybierz wolne miejsce stykające się bokiem z co najmniej jednym kafelkiem krajobrazu. Sam styk narożnikami nie tworzy połączenia.",
          "Obróć kafelek, jeśli chcesz. Nie zmieniaj jednak jego grafiki ani położenia elementów na kafelku.",
          "Sprawdź każdy dotykający bok, nie tylko pierwszy wybrany. Miasto musi łączyć się z miastem, droga z drogą, a pole z polem.",
          "Połóż kafelek dopiero wtedy, gdy wszystkie jego styki są zgodne. Następnie przejdź do decyzji o figurze."
        ]
      },
      {
        "type": "paragraph",
        "text": "Kafelek może mieć więcej niż jednego sąsiada. Wtedy jego wszystkie przylegające boki muszą pasować równocześnie. Nie wolno zaakceptować dobrego połączenia z jednej strony, jeśli po drugiej stronie droga dochodzi do czystego pola albo mur miasta do pola."
      },
      {
        "type": "warning",
        "text": "Jeśli kafelka nie da się dołożyć nigdzie, odłóż go i dobierz nowy."
      },
      {
        "type": "example",
        "title": "Pełny przykład: dwa sąsiednie boki",
        "text": "Bartek chce umieścić kafelek w wolnej luce między kafelkiem z drogą po lewej i kafelkiem z miastem nad nim. Obraca kafelek tak, aby jego droga łączyła się z drogą po lewej, lecz jego górny bok nadal pokazuje pole. Ruch jest nielegalny, mimo że lewy bok pasuje. Bartek obraca kafelek ponownie; teraz droga styka się z drogą, a miasto z miastem. Dopiero tę orientację może położyć."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Celina dobrała kafelek z drogą i może położyć go obok dwóch istniejących kafelków.",
            "Sprawdza wszystkie styki przed odłożeniem kafelka.",
            "1. Wybiera wolne miejsce przylegające bokiem. 2. Obraca kafelek. 3. Porównuje oba dotykające boki. 4. Odkłada go tylko w zgodnej orientacji.",
            "Krajobraz pozostaje legalny, a Celina może przejść do etapu figury.",
            "Każdy stykający się bok jest warunkiem legalności, nawet gdy kafelek dotyka kilku sąsiadów."
          ]
        ]
      },
      {
        "type": "heading",
        "text": "Błędy i wyjątki",
        "level": 2
      },
      {
        "type": "commonMistake",
        "text": "Sprawdzenie tylko jednego boku kafelka. Korekta: po wybraniu miejsca obejrzyj wszystkie boki, które stykają się z krajobrazem."
      },
      {
        "type": "commonMistake",
        "text": "Uznanie styku narożnikami za połączenie. Korekta: obszary łączą się wyłącznie przez wspólny bok."
      },
      {
        "type": "exception",
        "text": "Jeżeli po sprawdzeniu wszystkich orientacji kafelek nie ma żadnego legalnego miejsca, odkłada się go na bok i dobiera nowy. Nie wolno pozostawić go w ręce ani wymuszać niezgodnego połączenia."
      },
      {
        "type": "paragraph",
        "text": "Niektóre rozszerzenia wprowadzają własne elementy terenu lub szczególne ograniczenia. Stosuj je tylko wtedy, gdy dany moduł jest aktywny; nadal najpierw sprawdzaj podstawowe dopasowanie wszystkich zwykłych krawędzi."
      },
      {
        "type": "crossReference",
        "sectionId": "meeple-placement",
        "label": "Po legalnym dołożeniu: wystawianie figury"
      },
      {
        "type": "crossReference",
        "sectionId": "turn-structure",
        "label": "Wróć do kolejności całej tury"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Nowy kafelek musi stykać się bokiem z krajobrazem.",
          "Każdy stykający się bok musi pokazywać zgodny teren.",
          "Możesz obracać kafelek przed położeniem.",
          "Kafelek bez legalnego miejsca odkładasz i dobierasz nowy."
        ]
      },
      {
        "type": "heading",
        "text": "Pytania na start",
        "level": 2
      },
      {
        "type": "qa",
        "question": "Czy mogę położyć kafelek tak, aby dotykał krajobrazu tylko rogiem?",
        "answer": "Nie. Kafelek musi stykać się z istniejącym krajobrazem co najmniej jednym całym bokiem."
      },
      {
        "type": "qa",
        "question": "Czy mogę obrócić dobrany kafelek?",
        "answer": "Tak. Przed położeniem możesz szukać legalnej orientacji przez obracanie kafelka."
      },
      {
        "type": "qa",
        "question": "Czy mogę położyć kafelek, jeśli pasuje tylko do jednego z dwóch sąsiadów?",
        "answer": "Nie. Wszystkie boki stykające się z kafelkami krajobrazu muszą pasować jednocześnie."
      }
    ],
    "imageIds": [
      "base-edge-match"
    ]
  },
  {
    "id": "meeple-placement",
    "title": "Wystawianie figury: jak zajmować obszary",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3"
    ],
    "keywords": [
      "meeple",
      "pionek"
    ],
    "level": "basic",
    "hubGroup": "Tura",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "image",
        "imageId": "meeple-spots",
        "alt": "Legalne miejsca meepla.",
        "caption": "Miejsca meepla"
      },
      {
        "type": "paragraph",
        "text": "Po legalnym dołożeniu kafelka możesz wystawić jedną zwykłą figurę ze swojego zasobu. To moment, w którym wybierasz, z którego obszaru chcesz później zdobyć punkty. Figura może stać wyłącznie na właśnie dołożonym kafelku i tylko wtedy, gdy wybrany połączony obszar nie jest już zajęty przez żadną figurę. Zasada chroni przed prostym dokładaniem kolejnych własnych pionków do tej samej drogi lub miasta."
      },
      {
        "type": "paragraph",
        "text": "Na drodze figura jest podróżnikiem, w mieście rycerzem, w klasztorze mnichem, a na polu chłopem. Chłopa kładzie się, aby czytelnie odróżnić go od pozostałych ról. To, czy obszar jest zajęty, sprawdza się po całym jego dotychczasowym połączeniu, nie tylko na nowym kafelku. Dwie osobne drogi mogą być blisko siebie, ale jeśli nie są połączone, każda może mieć własną figurę."
      },
      {
        "type": "heading",
        "text": "Jak wystawić figurę krok po kroku",
        "level": 2
      },
      {
        "type": "numberedList",
        "items": [
          "Połóż kafelek legalnie.",
          "Zdecyduj, czy w ogóle chcesz użyć figury; możesz zrezygnować.",
          "Wybierz jeden obszar na nowym kafelku: drogę, miasto, klasztor albo pole.",
          "Prześledź połączenia tego obszaru z wcześniejszymi kafelkami. Jeśli znajduje się na nim jakakolwiek figura, nie możesz tam wystawić swojej.",
          "Jeżeli obszar jest wolny, weź jedną figurę z własnego zasobu i ustaw ją we właściwej pozycji."
        ]
      },
      {
        "type": "heading",
        "text": "Pełny przykład: zajęty obszar a wolny fragment",
        "level": 2
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Bartek ma rycerza w mieście. Celina dokłada kafelek, na którym jest fragment tego samego miasta oraz osobna droga.",
            "Celina nie może położyć rycerza w mieście, ale wystawia podróżnika na drodze.",
            "1. Dokłada kafelek. 2. Śledzi miasto do rycerza Bartka. 3. Odrzuca miasto jako zajęte. 4. Sprawdza, że droga nie łączy się z żadną figurą. 5. Wystawia podróżnika.",
            "Figura Celiny legalnie zajmuje drogę, a miasto pozostaje zajęte przez Bartka.",
            "Zajęcie dotyczy całego połączonego obszaru, lecz różne obszary na tym samym kafelku sprawdza się oddzielnie."
          ]
        ]
      },
      {
        "type": "heading",
        "text": "Błędy i wyjątki",
        "level": 2
      },
      {
        "type": "commonMistake",
        "text": "Sprawdzanie zajęcia tylko na nowym kafelku. Korekta: prześledź cały połączony obszar, zanim wystawisz figurę."
      },
      {
        "type": "commonMistake",
        "text": "Wystawienie figury na kafelku dołożonym w poprzedniej turze. Korekta: w podstawowej turze figura może stanąć wyłącznie na kafelku dołożonym właśnie teraz."
      },
      {
        "type": "warning",
        "text": "Nie wolno wystawić figury na połączonym obszarze, na którym stoi już jakakolwiek figura — również przeciwnika."
      },
      {
        "type": "paragraph",
        "text": "Późniejsze dołożenie kafelka może połączyć dwa wcześniej osobne, już zajęte obszary. To legalne, ponieważ wtedy nie wystawiasz figury bezpośrednio na zajętym obszarze; połączenie wpływa natomiast na późniejszą większość. Rozszerzenia mogą ustanawiać dodatkowe role lub wyjątki, opisane tylko w ich własnych rozdziałach."
      },
      {
        "type": "crossReference",
        "sectionId": "majority-ties",
        "label": "Połączenie zajętych obszarów: większość i remisy"
      },
      {
        "type": "crossReference",
        "sectionId": "reclaiming-meeples",
        "label": "Kiedy zajęta figura wraca do zasobu"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Możesz wystawić najwyżej jedną figurę po dołożeniu kafelka.",
          "Figura musi pochodzić z twojego zasobu i stanąć na nowym kafelku.",
          "Wybrany cały połączony obszar musi być wolny.",
          "Figura otrzymuje rolę zależną od obszaru, na którym stoi."
        ]
      },
      {
        "type": "heading",
        "text": "Pytania na start",
        "level": 2
      },
      {
        "type": "qa",
        "question": "Czy mogę położyć figurę na innym kafelku niż właśnie dołożony?",
        "answer": "Nie. W podstawowej turze figura może zostać wystawiona wyłącznie na kafelku dołożonym w tej turze."
      },
      {
        "type": "qa",
        "question": "Czy mogę zająć miasto, jeśli figura przeciwnika jest na jego odległym fragmencie?",
        "answer": "Nie, jeżeli fragmenty są już połączone w jedno miasto. Sprawdza się cały obszar, nie odległość między figurami."
      },
      {
        "type": "qa",
        "question": "Czy muszę wystawić figurę, gdy nowy obszar jest wolny?",
        "answer": "Nie. Wystawienie figury jest opcjonalne; możesz zachować ją na późniejszą turę."
      }
    ],
    "imageIds": [
      "meeple-spots"
    ]
  },
  {
    "id": "majority-ties",
    "title": "Większość i remisy: kto otrzymuje punkty",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3"
    ],
    "keywords": [
      "wiekszosc",
      "remis"
    ],
    "level": "basic",
    "hubGroup": "Punktowanie",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Gdy ukończony obszar ma figury kilku graczy, punkty nie są automatycznie dzielone. Porównuje się ich siłę na tym jednym obszarze. Zasada większości pozwala legalnie połączyć wcześniej oddzielne, zajęte obszary i rozstrzyga, komu należą się punkty."
      },
      {
        "type": "paragraph",
        "text": "Zwykła figura ma siłę 1. Gracz z największą łączną siłą na ukończonym obszarze otrzymuje pełną wartość punktową. Jeżeli największa siła jest remisowa, każdy remisujący gracz dostaje pełną wartość — punktów nie dzieli się. Nie liczy się, kto rozpoczął obszar ani kto dołożył zamykający kafelek."
      },
      {
        "type": "numberedList",
        "items": [
          "Po ukończeniu obszaru sprawdź wszystkie figury na tym połączonym obszarze.",
          "Zsumuj siłę każdego gracza wyłącznie na tym obszarze.",
          "Znajdź największy wynik.",
          "Przyznaj pełne punkty każdemu graczowi z tym wynikiem, a następnie zwróć jego figury z ukończonego obszaru."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna i Bartek mają po jednej zwykłej figurze w dwóch częściach miasta. Celina dokłada kafelek, który je łączy i zamyka.",
            "Celina rozpatruje ukończone miasto po swoim etapie wystawienia.",
            "1. Miasto jest ukończone. 2. Anna ma siłę 1, Bartek ma siłę 1. 3. Największa siła wynosi 1 i jest remisowa. 4. Każde otrzymuje pełną wartość miasta.",
            "Anna i Bartek otrzymują po pełnej liczbie punktów; ich figury wracają.",
            "Przy remisie wszyscy gracze z największą siłą otrzymują pełną wartość, a nie jej część."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Dzielenie punktów między remisujących graczy. Korekta: każdy remisujący z największą siłą dostaje pełną wartość ukończonego obszaru."
      },
      {
        "type": "commonMistake",
        "text": "Przyznanie punktów osobie, która zamknęła obszar. Korekta: decyduje siła figurek na obszarze, nie autor ostatniego kafelka."
      },
      {
        "type": "paragraph",
        "text": "Aktywne rozszerzenia mogą dodać figury o innej sile albo figury, które nie liczą się do większości. Wtedy stosuj ich własny opis, ale zawsze porównuj siłę tylko w obrębie punktowanego obszaru."
      },
      {
        "type": "crossReference",
        "sectionId": "meeple-placement",
        "label": "Jak legalnie powstają połączone zajęte obszary"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Punktuje najwyższa siła na ukończonym obszarze.",
          "Zwykła figura ma siłę 1.",
          "Remis oznacza pełne punkty dla każdego remisującego.",
          "Nie dziel punktów i nie patrz na autora ostatniego kafelka."
        ]
      },
      {
        "type": "qa",
        "question": "Czy dwaj remisujący gracze dzielą punkty miasta po połowie?",
        "answer": "Nie. Każdy gracz z największą siłą dostaje pełną wartość tego miasta."
      }
    ]
  },
  {
    "id": "roads",
    "title": "Drogi: ukończenie i punktowanie",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3"
    ],
    "keywords": [
      "droga"
    ],
    "level": "basic",
    "hubGroup": "Punktowanie",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Droga jest jednym z najszybszych sposobów odzyskania figury i zdobycia punktów. W tym rozdziale nauczysz się rozpoznać całą połączoną drogę, określić jej koniec oraz policzyć wynik bez pomijania kafelków. Zasada drogi pokazuje też podstawowy schemat punktowania: ukończ obszar, sprawdź większość, przyznaj punkty i zwróć figury."
      },
      {
        "type": "paragraph",
        "text": "Droga jest ukończona, gdy każdy jej koniec dochodzi do zamknięcia albo gdy sama tworzy pętlę. Zakończeniem może być miejsce, w którym grafika drogi się kończy; nie musi to być osobny rodzaj kafelka. Po dołożeniu kafelka sprawdź całą drogę od jednego końca do drugiego, a nie tylko fragment na nowym kafelku."
      },
      {
        "type": "scoringTable",
        "headers": [
          "Sytuacja",
          "Punkty"
        ],
        "rows": [
          [
            "Ukończona",
            "1/kafelek"
          ],
          [
            "Nieukończona na końcu",
            "1/kafelek"
          ]
        ]
      },
      {
        "type": "heading",
        "text": "Jak rozpatrzyć ukończoną drogę",
        "level": 2
      },
      {
        "type": "numberedList",
        "items": [
          "Po dołożeniu kafelka sprawdź, czy droga ma jeszcze otwarte wyjście.",
          "Jeśli jest ukończona, policz każdy kafelek należący do tej drogi dokładnie raz.",
          "Sprawdź większość figurek na całej drodze.",
          "Przyznaj zwycięzcy albo remisującym po 1 punkcie za każdy kafelek, a ich figury zwróć do zasobu."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna ma podróżnika na drodze z trzech kafelków i jednym otwartym końcem.",
            "Bartek dokłada czwarty kafelek, który zamyka ostatni koniec drogi.",
            "1. Droga jest ukończona. 2. Liczy się cztery kafelki. 3. Anna ma największą siłę na drodze. 4. Anna otrzymuje 4 punkty i odzyskuje podróżnika.",
            "Anna dostaje 4 punkty.",
            "Ukończona droga daje 1 punkt za każdy należący do niej kafelek, niezależnie od tego, kto położył ostatni."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Liczenie tylko kafelków dołożonych po wystawieniu podróżnika. Korekta: po ukończeniu liczy się każdy kafelek całej drogi."
      },
      {
        "type": "commonMistake",
        "text": "Zostawienie podróżnika na ukończonej drodze. Korekta: po przyznaniu punktów figura wraca do zasobu właściciela."
      },
      {
        "type": "paragraph",
        "text": "Nieukończona droga pozostaje na planszy do końca gry i wtedy również daje 1 punkt za każdy swój kafelek, lecz jej figura nie wraca przed końcowym punktowaniem. Aktywne rozszerzenia mogą zmienić wartość lub dodać warunki drogi; stosuj je tylko wtedy, gdy odpowiedni moduł jest włączony."
      },
      {
        "type": "crossReference",
        "sectionId": "majority-ties",
        "label": "Jeżeli na drodze jest więcej niż jedna figura: większość i remisy"
      },
      {
        "type": "crossReference",
        "sectionId": "end-scoring",
        "label": "Nieukończone drogi podczas punktowania końcowego"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Droga jest ukończona po zamknięciu obu końców albo utworzeniu pętli.",
          "Ukończona droga daje 1 punkt za każdy kafelek.",
          "Po punktowaniu podróżnik wraca do zasobu.",
          "Nieukończona droga na końcu gry także daje 1 punkt za kafelek."
        ]
      },
      {
        "type": "qa",
        "question": "Czy droga tworząca pętlę jest ukończona?",
        "answer": "Tak. Pętla nie ma otwartych końców, więc punktuje jak ukończona droga."
      },
      {
        "type": "qa",
        "question": "Czy autor ostatniego kafelka dostaje punkty za drogę?",
        "answer": "Tylko jeśli ma na niej największą siłę. O punktach decydują figury na drodze, nie autor zamknięcia."
      }
    ]
  },
  {
    "id": "cities",
    "title": "Miasta: mury, herby i punktowanie",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3"
    ],
    "keywords": [
      "miasto",
      "herb"
    ],
    "level": "basic",
    "hubGroup": "Punktowanie",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Miasto jest zwykle bardziej wartościowe niż droga, ale może długo wiązać rycerza. Poznasz tu warunek ukończenia miasta, rolę herbów oraz różnicę między punktowaniem w trakcie gry i na końcu. Te zasady pomagają zdecydować, kiedy ryzykować większe miasto, a kiedy odzyskać figurę szybciej."
      },
      {
        "type": "paragraph",
        "text": "Miasto jest ukończone, gdy żadna jego krawędź miasta nie pozostaje otwarta. Prześledź mur przez wszystkie połączone kafelki: miasto może być duże, zawierać kilka kafelków i mieć więcej niż jednego rycerza. Herb widoczny w mieście jest premią do jego punktacji, a nie osobnym obszarem."
      },
      {
        "type": "scoringTable",
        "headers": [
          "Sytuacja",
          "Punkty"
        ],
        "rows": [
          [
            "Ukończone",
            "2/kafelek + 2/herb"
          ],
          [
            "Nieukończone na końcu",
            "1/kafelek + 1/herb"
          ]
        ]
      },
      {
        "type": "heading",
        "text": "Jak policzyć ukończone miasto",
        "level": 2
      },
      {
        "type": "numberedList",
        "items": [
          "Sprawdź, czy miasto nie ma otwartych krawędzi.",
          "Policz każdy kafelek tego miasta oraz każdy widoczny w nim herb.",
          "Ustal większość rycerzy na całym mieście.",
          "Gracz z większością otrzymuje 2 punkty za kafelek i 2 punkty za herb; przy remisie pełną wartość otrzymuje każdy remisujący.",
          "Zwróć rycerzy z ukończonego miasta do zasobów."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna ma rycerza w ukończonym właśnie mieście z trzema kafelkami i jednym herbem.",
            "Bartek dokłada kafelek, który zamyka ostatnią otwartą krawędź miasta.",
            "1. Miasto nie ma otwartych krawędzi. 2. Liczy się 3 kafelki i 1 herb. 3. Anna ma większość. 4. Anna otrzymuje 2×3 + 2×1 punktów i odzyskuje rycerza.",
            "Anna otrzymuje 8 punktów.",
            "W trakcie gry ukończone miasto daje 2 punkty za każdy kafelek oraz 2 za każdy herb."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Pominięcie herbu, bo nie jest osobnym kafelkiem. Korekta: każdy herb w ukończonym mieście daje dodatkowe 2 punkty."
      },
      {
        "type": "commonMistake",
        "text": "Uznanie miasta za ukończone, gdy wygląda na otoczone. Korekta: sprawdź każdą krawędź miasta; pojedyncza otwarta krawędź oznacza, że miasto nie jest ukończone."
      },
      {
        "type": "paragraph",
        "text": "Nieukończone miasto na końcu gry również punktuje, lecz słabiej: po 1 punkcie za kafelek i herb. Rozszerzenia mogą zmienić punktację określonych miast lub dodać szczególne figury; stosuj te zmiany wyłącznie przy aktywnym module."
      },
      {
        "type": "crossReference",
        "sectionId": "majority-ties",
        "label": "Kilku rycerzy w jednym mieście: większość i remisy"
      },
      {
        "type": "crossReference",
        "sectionId": "end-scoring",
        "label": "Nieukończone miasta na końcu gry"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Miasto jest ukończone bez otwartych krawędzi miasta.",
          "W trakcie gry: 2 punkty za kafelek i 2 za herb.",
          "Na końcu gry: 1 punkt za kafelek i 1 za herb.",
          "Po punktowaniu ukończonego miasta rycerze wracają."
        ]
      },
      {
        "type": "qa",
        "question": "Czy herb daje punkty także w nieukończonym mieście na końcu gry?",
        "answer": "Tak. Podczas punktowania końcowego każdy herb w nieukończonym mieście jest wart 1 punkt."
      }
    ]
  },
  {
    "id": "monasteries",
    "title": "Klasztory: otoczenie ośmioma kafelkami",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3"
    ],
    "keywords": [
      "klasztor"
    ],
    "level": "basic",
    "hubGroup": "Punktowanie",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Klasztor punktuje dzięki kafelkom wokół niego, a nie dzięki drogom lub miastom narysowanym na sąsiadach. W tym rozdziale nauczysz się liczyć wszystkie osiem pozycji, także ukośne, oraz rozpoznać moment, w którym mnich wraca do zasobu."
      },
      {
        "type": "paragraph",
        "text": "Klasztor jest ukończony, gdy jego własny kafelek ma kafelek na każdej z ośmiu przylegających pozycji: nad, pod, po bokach i po przekątnych. Rodzaj terenu na sąsiednim kafelku nie ma znaczenia. Klasztor może zostać ukończony ruchem dowolnego gracza, a punktuje właściciel mnicha."
      },
      {
        "type": "scoringTable",
        "headers": [
          "Sytuacja",
          "Punkty"
        ],
        "rows": [
          [
            "Ukończony",
            "9"
          ],
          [
            "Nieukończony na końcu",
            "1 + obecni sąsiedzi"
          ]
        ]
      },
      {
        "type": "numberedList",
        "items": [
          "Po każdym dołożeniu kafelka sprawdź klasztory, których otoczenie mogło się zmienić.",
          "Policz kafelek klasztoru i wszystkie obecne osiem sąsiednich pozycji.",
          "Przy dziewięciu kafelkach łącznie klasztor jest ukończony.",
          "Właściciel mnicha otrzymuje 9 punktów i odzyskuje figurę."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Celina ma mnicha w klasztorze otoczonym siedmioma kafelkami; brakuje jednego pola po przekątnej.",
            "Anna dokłada kafelek na brakującej ukośnej pozycji.",
            "1. Liczy się kafelek klasztoru i osiem sąsiadów. 2. Jest ich łącznie 9. 3. Klasztor jest ukończony. 4. Celina dostaje 9 punktów i odzyskuje mnicha.",
            "Celina zdobywa 9 punktów.",
            "Pozycje ukośne liczą się tak samo jak pozycje przylegające bokiem."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Liczenie wyłącznie czterech kafelków stykających się bokiem. Korekta: klasztor wymaga wszystkich ośmiu sąsiednich pozycji, także po przekątnych."
      },
      {
        "type": "commonMistake",
        "text": "Czekanie na turę właściciela mnicha. Korekta: klasztor punktuje natychmiast po dołożeniu kafelka, który kończy jego otoczenie, niezależnie od wykonawcy ruchu."
      },
      {
        "type": "paragraph",
        "text": "Na końcu gry nieukończony klasztor daje 1 punkt za swój kafelek oraz 1 punkt za każdego obecnego sąsiada. Opat i ogrody mają własne reguły, choć ich punktowanie korzysta z podobnego liczenia otoczenia."
      },
      {
        "type": "crossReference",
        "sectionId": "abbot-rules",
        "label": "Opat i ogród: osobna figura oraz zasady"
      },
      {
        "type": "crossReference",
        "sectionId": "end-scoring",
        "label": "Nieukończone klasztory na końcu gry"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Klasztor liczy osiem sąsiednich pozycji, w tym przekątne.",
          "Ukończony klasztor daje 9 punktów.",
          "Mnich wraca po punktowaniu ukończonego klasztoru.",
          "Nieukończony klasztor na końcu daje punkty za siebie i obecnych sąsiadów."
        ]
      },
      {
        "type": "qa",
        "question": "Czy kafelek po przekątnej pomaga ukończyć klasztor?",
        "answer": "Tak. Wszystkie osiem pozycji wokół klasztoru, w tym cztery przekątne, liczy się do jego ukończenia."
      }
    ]
  },
  {
    "id": "fields",
    "title": "Pola i chłopi: punktowanie na końcu gry",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3"
    ],
    "keywords": [
      "pole",
      "chlop"
    ],
    "level": "intermediate",
    "hubGroup": "Punktowanie",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Pola są najbardziej odroczonym elementem gry: chłop zwykle pozostaje na nich do końca, a ich wynik oblicza się dopiero po ostatniej turze. Nauczysz się odróżniać pola rozdzielone drogami i miastami, przypisywać im ukończone miasta oraz unikać wielokrotnego liczenia tego samego miasta."
      },
      {
        "type": "paragraph",
        "text": "Chłopa kładzie się na polu. Pole to połączony obszar otwartego terenu, a jego granice wyznaczają drogi i miasta. Pole nie punktuje w trakcie gry, nawet gdy sąsiadujące miasto właśnie zostało ukończone. Podczas punktowania końcowego porównuje się większość chłopów na każdym polu."
      },
      {
        "type": "numberedList",
        "items": [
          "Wybierz jedno pole i sprawdź wszystkie połączone fragmenty tego samego terenu.",
          "Ustal większość chłopów na tym polu.",
          "Zidentyfikuj ukończone miasta stykające się z tym polem.",
          "Policz po 3 punkty za każde takie miasto, pamiętając, że w obrębie jednego pola każde miasto liczy się tylko raz.",
          "Przyznaj pełną wartość każdemu remisującemu z największą siłą, potem przejdź do następnego pola."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna ma chłopa na polu stykającym się z dwoma ukończonymi miastami. Jedno z miast dotyka pola w kilku miejscach.",
            "Podczas końcowego punktowania Anna rozpatruje to jedno pole.",
            "1. Ustala większość Anny. 2. Rozpoznaje dwa różne ukończone miasta. 3. Liczy każde tylko raz. 4. Otrzymuje 3 + 3 punkty.",
            "Anna otrzymuje 6 punktów za pole.",
            "W jednym polu to samo miasto liczy się raz, nawet gdy jego mur styka się z polem w kilku miejscach."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Punktowanie pola, gdy ukończone zostaje sąsiednie miasto. Korekta: pola punktują wyłącznie na końcu całej gry."
      },
      {
        "type": "commonMistake",
        "text": "Liczenie jednego miasta wiele razy, bo dotyka pola wieloma bokami. Korekta: na jednym polu każde ukończone miasto jest warte dokładnie 3 punkty."
      },
      {
        "type": "commonMistake",
        "text": "Traktowanie pól po obu stronach drogi jako jednego pola. Korekta: droga lub miasto może rozdzielać pola; prześledź faktyczne połączenia terenu."
      },
      {
        "type": "paragraph",
        "text": "To samo ukończone miasto może punktować dla kilku różnych pól, ponieważ każde pole rozpatruje się osobno. Aktywne rozszerzenia mogą zmieniać wartość miast dla pól albo dodawać premie; podstawowa metoda ustalania pola i większości pozostaje punktem wyjścia."
      },
      {
        "type": "crossReference",
        "sectionId": "majority-ties",
        "label": "Większość chłopów i remisy"
      },
      {
        "type": "crossReference",
        "sectionId": "end-scoring",
        "label": "Pełna kolejność punktowania końcowego"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Pola punktują wyłącznie na końcu gry.",
          "Każde ukończone miasto stykające się z polem daje 3 punkty.",
          "W jednym polu miasto liczy się tylko raz.",
          "Jedno miasto może punktować dla kilku różnych pól."
        ]
      },
      {
        "type": "qa",
        "question": "Czy ukończone miasto punktuje dla pola w trakcie gry?",
        "answer": "Nie. Miasto może zostać ukończone wcześniej, ale jego wartość dla pola liczy się dopiero podczas punktowania końcowego."
      }
    ]
  },
  {
    "id": "mid-scoring",
    "title": "Punktowanie w trakcie gry: zamykanie obszarów",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3"
    ],
    "keywords": [
      "punktowanie"
    ],
    "level": "basic",
    "hubGroup": "Punktowanie",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Punktowanie w trakcie gry następuje po każdym dołożonym kafelku, po decyzji o wystawieniu figury. Nie czeka na turę właściciela obszaru. Ten etap zamienia ukończone drogi, miasta i klasztory w punkty oraz odzyskane figury."
      },
      {
        "type": "numberedList",
        "items": [
          "Po etapie figury sprawdź drogi, miasta i klasztory zmienione przez nowy kafelek.",
          "Dla każdego ukończonego obszaru ustal większość i policz punkty zgodnie z jego zasadą.",
          "Przesuń znaczniki punktacji wszystkich graczy, którzy otrzymali punkty.",
          "Zwróć do zasobów figury z każdego zapunktowanego ukończonego obszaru."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Kafelek Bartka jednocześnie kończy drogę Anny i otoczenie klasztoru Celiny.",
            "Po etapie figury Bartek rozpatruje oba ukończenia.",
            "1. Punktuje drogę Anny. 2. Zwraca podróżnika. 3. Punktuje klasztor Celiny. 4. Zwraca mnicha.",
            "Obie właścicielki dostają punkty i odzyskują figury.",
            "Jeden kafelek może ukończyć kilka niezależnych obszarów; każdy rozpatruje się osobno."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Punktowanie tylko obszaru aktywnego gracza. Korekta: sprawdź wszystkie ukończenia wywołane kafelkiem."
      },
      {
        "type": "crossReference",
        "sectionId": "resolution-order-base",
        "label": "Kolejność rozpatrywania"
      },
      {
        "type": "crossReference",
        "sectionId": "roads",
        "label": "Punktowanie dróg"
      },
      {
        "type": "crossReference",
        "sectionId": "cities",
        "label": "Punktowanie miast"
      },
      {
        "type": "crossReference",
        "sectionId": "monasteries",
        "label": "Punktowanie klasztorów"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Punktowanie następuje po decyzji o figurze.",
          "Sprawdza się wszystkie ukończenia wywołane kafelkiem.",
          "Pola czekają do końca gry.",
          "Po punktowaniu figury wracają."
        ]
      },
      {
        "type": "qa",
        "question": "Czy jeden kafelek może dać punkty za więcej niż jeden obszar?",
        "answer": "Tak. Każda droga, miasto lub klasztor ukończone przez ten kafelek jest rozpatrywane osobno."
      }
    ]
  },
  {
    "id": "end-scoring",
    "title": "Punktowanie końcowe: policz ostatnie punkty",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3"
    ],
    "keywords": [
      "koniec"
    ],
    "level": "basic",
    "hubGroup": "Koniec gry",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Po rozegraniu ostatniego kafelka nie usuwa się nieukończonych obszarów ani nie uznaje ich za bezwartościowe. Punktowanie końcowe daje punkty za pozostałe drogi, miasta, klasztory i pola. Najpierw rozstrzygnij wszystkie zwykłe obszary, a pola zostaw na później, ponieważ zależą od ukończonych miast."
      },
      {
        "type": "resolutionOrder",
        "title": "Kolejność rozstrzygania",
        "steps": [
          "Efekty rozszerzeń przed zwykłym końcem.",
          "Nieukończone drogi.",
          "Nieukończone miasta.",
          "Klasztory i ogrody.",
          "Figury i punkty rozszerzeń.",
          "Pola.",
          "Towary i kolekcje.",
          "Końcowe premie."
        ]
      },
      {
        "type": "paragraph",
        "text": "W samej grze podstawowej rozpatrujesz przede wszystkim drogi, miasta, klasztory i pola. Nieukończona droga daje 1 punkt za każdy kafelek. Nieukończone miasto daje 1 punkt za kafelek i 1 za herb. Klasztor daje 1 punkt za własny kafelek oraz za każdego obecnego sąsiada. Na końcu policz pola: 3 punkty za każde ukończone miasto stykające się z polem."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Po ostatniej turze Anna ma nieukończoną drogę, Bartek nieukończone miasto z herbem, a Celina chłopa na polu.",
            "Gracze liczą wszystkie pozostałe obszary przed porównaniem wyników.",
            "1. Punktują drogę Anny. 2. Punktują miasto Bartka po 1 za kafelek i herb. 3. Punktują pole Celiny za sąsiadujące ukończone miasta. 4. Porównują końcowe wyniki.",
            "Każdy otrzymuje punkty tylko za swoje kontrolowane obszary, a zwycięża najwyższy łączny wynik.",
            "Nieukończone obszary nie przepadają; pola rozpatruje się po miastach."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Pomijanie nieukończonych obszarów. Korekta: wszystkie pozostałe drogi, miasta i klasztory punktują na końcu według obniżonych lub częściowych wartości."
      },
      {
        "type": "commonMistake",
        "text": "Liczenie pól przed ustaleniem ukończonych miast. Korekta: najpierw rozstrzygnij zwykłe obszary, następnie pola."
      },
      {
        "type": "paragraph",
        "text": "Aktywne rozszerzenia mogą wprowadzać dodatkowe końcowe nagrody i własną kolejność. Dodaj je w miejscach wskazanych przez ich rozdziały, ale nie zmieniaj podstawowych wartości bez aktywnego modułu."
      },
      {
        "type": "crossReference",
        "sectionId": "roads",
        "label": "Wartość dróg na końcu gry"
      },
      {
        "type": "crossReference",
        "sectionId": "cities",
        "label": "Wartość miast na końcu gry"
      },
      {
        "type": "crossReference",
        "sectionId": "fields",
        "label": "Punktowanie pól"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Nieukończone obszary nadal punktują.",
          "Drogi: 1 za kafelek; miasta: 1 za kafelek i herb.",
          "Klasztor: 1 za siebie i każdy obecny sąsiad.",
          "Pola punktują na końcu po 3 za ukończone miasto."
        ]
      },
      {
        "type": "qa",
        "question": "Czy nieukończone miasto daje punkty za herb?",
        "answer": "Tak. Podczas punktowania końcowego każdy kafelek miasta i każdy herb jest wart 1 punkt."
      }
    ]
  },
  {
    "id": "resolution-order-base",
    "title": "Kolejność rozpatrywania efektów w turze",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "wica-order",
      "corpus-base"
    ],
    "keywords": [
      "kolejnosc"
    ],
    "level": "advanced",
    "hubGroup": "Tura",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Gdy aktywne są rozszerzenia, jedna tura może zawierać efekty przed dołożeniem, podczas dokładania, przy figurze i po punktowaniu. Ten rozdział ustala bezpieczną kolejność: najpierw efekty przed kafelkiem, potem dołożenie, potem figura albo akcja zamiast figury, potem punktowanie ukończeń, a na końcu efekty po punktowaniu."
      },
      {
        "type": "resolutionOrder",
        "title": "Bezpieczna kolejność",
        "steps": [
          "Efekty przed dołożeniem kafelka (np. smok, złodziej, oblężenie).",
          "Dobór, obrót i legalne dołożenie kafelka.",
          "Wystawienie figury albo akcja zamiast wystawienia (np. wycofanie Opata).",
          "Punktowanie wszystkich ukończonych dróg, miast i klasztorów oraz zwrot figurek.",
          "Efekty po punktowaniu wskazane przez aktywne dodatki."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Bartek dobrał kafelek ze smokiem, który jednocześnie zamyka drogę Anny.",
            "Najpierw rusza smokiem, potem dokłada kafelek i punktuje.",
            "1. Dwa ruchy smoka. 2. Dołożenie kafelka. 3. Etap figury. 4. Punktowanie drogi Anny.",
            "Smok działa przed zmianą krajobrazu, a droga punktuje dopiero po pełnym dołożeniu.",
            "Efekty przed dołożeniem zawsze wyprzedzają punktowanie ukończeń."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Punktowanie przed efektami, które muszą nastąpić przed dołożeniem. Korekta: najpierw efekty przed kafelkiem, potem dołożenie i dopiero punktowanie."
      },
      {
        "type": "crossReference",
        "sectionId": "turn-structure",
        "label": "Podstawowy przebieg tury"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Najpierw efekty przed dołożeniem.",
          "Potem kafelek, figura i punktowanie.",
          "Na końcu efekty po punktowaniu.",
          "Aktywne dodatki doprecyzowują własne momenty."
        ]
      },
      {
        "type": "qa",
        "question": "Czy mogę najpierw punktować, a potem ruszyć smokiem?",
        "answer": "Nie. Symbol smoka rozpatruje się przed dołożeniem kafelka, a punktowanie dopiero po etapie figury."
      }
    ]
  },
  {
    "id": "examples-base",
    "title": "Przykłady podstawy",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "corpus-base"
    ],
    "keywords": [
      "przyklad"
    ],
    "level": "basic",
    "hubGroup": "Przykłady",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Te przykłady łączą kilka podstawowych zasad naraz: łączenie zajętych obszarów, odzyskanie figury w tej samej turze oraz punktowanie pól przez kilka obszarów."
      },
      {
        "type": "example",
        "title": "Połączenie zajętych dróg",
        "text": "Dwie drogi z meeplami łączą się — obie figury zostają; punktuje większość."
      },
      {
        "type": "example",
        "title": "Meeple w tej samej turze",
        "text": "Zamykasz miasto rycerzem — punktujesz i odzyskujesz go natychmiast."
      },
      {
        "type": "example",
        "title": "Jedno miasto, dwa pola",
        "text": "Ukończone miasto styka się z dwoma polami — każde może dostać 3 punkty."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna zamyka miasto własnym rycerzem.",
            "Punktuje w tej samej turze i odzyskuje figurę.",
            "1. Dokłada zamykający kafelek. 2. Opcjonalnie wystawia lub nie. 3. Punktuje miasto. 4. Rycerz wraca.",
            "Anna ma punkty i wolną figurę na następną turę.",
            "Ukończenie w trakcie gry zwraca figurę natychmiast po punktowaniu."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Zostawienie rycerza na ukończonym mieście do końca gry. Korekta: po punktowaniu ukończonego miasta figura wraca."
      },
      {
        "type": "crossReference",
        "sectionId": "majority-ties",
        "label": "Większość po połączeniu obszarów"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Połączenie zajętych obszarów tworzy jedną większość.",
          "Ukończenie w trakcie gry zwraca figurę.",
          "Jedno miasto może punktować dla kilku pól."
        ]
      },
      {
        "type": "qa",
        "question": "Czy po zamknięciu miasta rycerzem mogę użyć go jeszcze w tej samej turze?",
        "answer": "Nie. Wraca do zasobu po punktowaniu i jest dostępny dopiero w przyszłej turze."
      }
    ]
  },
  {
    "id": "reclaiming-meeples",
    "title": "Odzyskiwanie meepli: kiedy figura wraca",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3"
    ],
    "keywords": [
      "odzyskanie"
    ],
    "level": "basic",
    "hubGroup": "Tura",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Figura wraca do zasobu dopiero po zapunktowaniu ukończonej drogi, miasta lub klasztoru. Chłop na polu zwykle zostaje do końca gry, ponieważ pola punktują wyłącznie wtedy. Ta różnica decyduje, czy zachowujesz tempo gry, czy wiążesz figurę na długo."
      },
      {
        "type": "numberedList",
        "items": [
          "Zapunktuj ukończony obszar według jego zasady.",
          "Zwróć wszystkie figury z tego obszaru do zasobów właścicieli.",
          "Nie zwracaj chłopów, dopóki nie nastąpi punktowanie końcowe pól.",
          "Odzyskana figura jest dostępna dopiero w późniejszej turze."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Celina ma podróżnika na ukończonej drodze i chłopa na polu.",
            "Po punktowaniu drogi zwraca tylko podróżnika.",
            "1. Punktuje drogę. 2. Zwraca podróżnika. 3. Zostawia chłopa na polu.",
            "Celina odzyskuje jedną figurę; chłop czeka do finału.",
            "Tylko zapunktowane ukończone obszary zwalniają figury w trakcie gry."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Zdejmowanie chłopa po ukończeniu sąsiedniego miasta. Korekta: chłop wraca dopiero po punktowaniu końcowym pola, chyba że aktywny dodatek mówi inaczej."
      },
      {
        "type": "crossReference",
        "sectionId": "fields",
        "label": "Dlaczego chłopi zostają do końca"
      },
      {
        "type": "crossReference",
        "sectionId": "mid-scoring",
        "label": "Punktowanie w trakcie gry"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Ukończona droga, miasto lub klasztor zwraca figury.",
          "Chłopi zwykle zostają do końca.",
          "Odzyskana figura nie wraca na planszę w tej samej turze."
        ]
      },
      {
        "type": "qa",
        "question": "Czy chłop wraca, gdy ukończę sąsiednie miasto?",
        "answer": "Nie. Ukończenie miasta nie zwalnia chłopa; pola punktują dopiero na końcu gry."
      }
    ]
  },
  {
    "id": "game-end",
    "title": "Koniec gry: ostatni kafelek i zwycięstwo",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3"
    ],
    "keywords": [
      "koniec"
    ],
    "level": "basic",
    "hubGroup": "Koniec gry",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Gra nie kończy się w chwili dobrania ostatniego kafelka. Ostatni gracz musi rozegrać pełną turę: dołożyć kafelek, opcjonalnie wystawić figurę i rozpatrzyć każde ukończenie. Dopiero potem przechodzi się do punktowania końcowego wszystkich pozostałych obszarów."
      },
      {
        "type": "numberedList",
        "items": [
          "Rozegraj pełną turę z ostatnim kafelkiem.",
          "Rozpatrz wszystkie ukończenia wywołane tym kafelkiem.",
          "Wykonaj punktowanie końcowe (drogi, miasta, klasztory, potem pola i ewentualne kolekcje dodatków).",
          "Porównaj końcowe wyniki na torze; najwyższy wynik wygrywa.",
          "Przy remisie najwyższego wyniku gracze z tym wynikiem dzielą zwycięstwo."
        ]
      },
      {
        "type": "heading",
        "text": "Remis końcowy (zwycięstwo)",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Remis na ukończonym obszarze (kilku graczy z tą samą największą siłą) oznacza, że każdy z nich dostaje pełne punkty za ten obszar — to nie jest remis zwycięstwa. Remis zwycięstwa pojawia się dopiero po całym punktowaniu końcowym, gdy dwóch lub więcej graczy ma ten sam najwyższy wynik na torze. Wtedy wszyscy z tym wynikiem dzielą zwycięstwo; nie rozstrzyga się go liczbą ukończonych miast, wolnymi meeplami ani autorem ostatniego kafelka."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Celina dobiera ostatni kafelek, który kończy miasto Anny.",
            "Celina rozgrywa normalną turę, mimo że pula kafelków jest pusta.",
            "1. Dokłada kafelek. 2. Kończy etap figury. 3. Anna punktuje miasto i odzyskuje rycerza. 4. Gracze przechodzą do punktowania końcowego.",
            "Punkty za ukończone miasto naliczono według zasad gry, zanim rozpoczęto finał.",
            "Ostatni kafelek nie skraca tury; kończy ją dopiero pełne rozpatrzenie."
          ],
          [
            "Po punktowaniu końcowym Anna i Bartek mają po 87 punktów, Celina 72.",
            "Porównują wyłącznie końcowe wyniki na torze.",
            "1. Ustalają najwyższy wynik: 87. 2. Widzą dwóch graczy z 87. 3. Nie szukają dodatkowego tie-breakera.",
            "Anna i Bartek dzielą zwycięstwo; Celina zajmuje kolejne miejsce.",
            "Remis najwyższego wyniku kończy partię wspólnym zwycięstwem remisujących."
          ]
        ]
      },
      {
        "type": "warning",
        "text": "Nie zaczynaj punktowania końcowego w środku ostatniej tury."
      },
      {
        "type": "commonMistake",
        "text": "Zakończenie gry po dobraniu ostatniego kafelka. Korekta: ostatni kafelek musi zostać legalnie dołożony i w pełni rozpatrzony."
      },
      {
        "type": "commonMistake",
        "text": "Rozstrzyganie remisu końcowego liczbą miast albo wolnych figurek. Korekta: przy tym samym najwyższym wyniku gracze dzielą zwycięstwo."
      },
      {
        "type": "crossReference",
        "sectionId": "end-scoring",
        "label": "Następnie: pełne punktowanie końcowe"
      },
      {
        "type": "crossReference",
        "sectionId": "majority-ties",
        "label": "Remisy siły na obszarze (pełne punkty, nie dzielone)"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Ostatni kafelek rozgrywa się jak każda inna tura.",
          "Po nim rozpatruje się ukończenia, potem punktowanie końcowe.",
          "Najwyższy końcowy wynik wygrywa.",
          "Remis najwyższego wyniku = wspólne zwycięstwo remisujących."
        ]
      },
      {
        "type": "qa",
        "question": "Czy po ostatnim kafelku wolno jeszcze zdobyć punkty za ukończone miasto?",
        "answer": "Tak. Każde ukończenie wywołane ostatnim kafelkiem rozpatruje się w jego normalnej turze, przed punktowaniem końcowym."
      },
      {
        "type": "qa",
        "question": "Co jeśli dwóch graczy ma ten sam najwyższy wynik?",
        "answer": "Dzielą zwycięstwo. Nie stosuje się dodatkowych kryteriów rozstrzygających, takich jak liczba miast czy wolnych meepli."
      }
    ]
  },
  {
    "id": "scoring-table-base",
    "title": "Tabela punktowania podstawy",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3"
    ],
    "keywords": [
      "tabela",
      "punkty"
    ],
    "level": "basic",
    "hubGroup": "Punktowanie",
    "content": [
      {
        "type": "heading",
        "text": "Po co jest ta tabela",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Ta tabela zbiera wartości punktowe gry podstawowej w jednym miejscu. Nie zastępuje rozdziałów o drogach, miastach, klasztorach i polach — tam znajdziesz warunki ukończenia, większość i przykłady. Tutaj szybko sprawdzisz, ile punktów daje każdy rodzaj obszaru w trakcie gry i na końcu."
      },
      {
        "type": "image",
        "imageId": "base-scoring",
        "alt": "Skrót punktowania podstawy Carcassonne.",
        "caption": "Punktowanie podstawy"
      },
      {
        "type": "note",
        "text": "Pełna, wspólna tabela punktowania (baza + aktywne dodatki) jest w Szybkiej ściądze — jedna tabela filtrowana po konfiguracji partii."
      },
      {
        "type": "scoringTable",
        "headers": [
          "Element",
          "W trakcie",
          "Koniec"
        ],
        "rows": [
          [
            "Droga",
            "1/kafelek",
            "1/kafelek"
          ],
          [
            "Miasto",
            "2+2/herb",
            "1+1/herb"
          ],
          [
            "Klasztor",
            "9",
            "1+sąsiedzi"
          ],
          [
            "Pole",
            "—",
            "3/miasto"
          ]
        ]
      },
      {
        "type": "paragraph",
        "text": "Droga ma tę samą wartość w trakcie i na końcu: 1 punkt za każdy kafelek drogi. Miasto w trakcie gry daje 2 punkty za kafelek i 2 za herb; nieukończone na końcu — po 1 za kafelek i herb. Ukończony klasztor w trakcie gry daje zawsze 9 punktów; nieukończony na końcu — 1 za siebie i 1 za każdego obecnego sąsiada. Pole nigdy nie punktuje w trakcie gry: tylko na końcu, 3 punkty za każde ukończone miasto stykające się z polem."
      },
      {
        "type": "commonMistake",
        "text": "Liczenie pól w trakcie gry albo dawanie 2 punktów za nieukończone miasto na końcu. Korekta: pola tylko na końcu; nieukończone miasto ma wartości 1+1."
      },
      {
        "type": "crossReference",
        "sectionId": "mid-scoring",
        "label": "Kiedy punktuje się w trakcie tury"
      },
      {
        "type": "crossReference",
        "sectionId": "end-scoring",
        "label": "Pełne punktowanie końcowe"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Droga: zawsze 1 za kafelek.",
          "Miasto: 2+2 gdy ukończone; 1+1 gdy nieukończone na końcu.",
          "Klasztor: 9 gdy ukończony; inaczej 1 + sąsiedzi.",
          "Pole: tylko na końcu, 3 za ukończone miasto."
        ]
      },
      {
        "type": "qa",
        "question": "Czy herb w nieukończonym mieście na końcu gry jest wart 2 punkty?",
        "answer": "Nie. Podczas punktowania końcowego każdy kafelek miasta i każdy herb jest wart 1 punkt."
      }
    ],
    "imageIds": [
      "base-scoring"
    ]
  },
  {
    "id": "common-patterns",
    "title": "Najczęstsze błędy gry podstawowej",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "corpus-base",
      "polish-base-c3"
    ],
    "keywords": [
      "bledy",
      "pułapki",
      "poczatkujacy"
    ],
    "level": "basic",
    "hubGroup": "Przykłady",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Poniżej zebrano błędy, które najczęściej psują pierwszą partię Carcassonne. Każdy punkt opisuje złą praktykę, poprawną regułę i skutek przy stole. Rozdział nie zastępuje szczegółowych rozdziałów o drogach czy polach — służy jako szybka kontrola przed i w trakcie gry."
      },
      {
        "type": "heading",
        "text": "Dokładanie i figury",
        "level": 2
      },
      {
        "type": "commonMistake",
        "text": "Dokładanie kafelka tak, by teren nie stykał się z sąsiadem tego samego typu. Korekta: każda stykająca się krawędź musi pasować — droga do drogi, miasto do miasta, pole do pola."
      },
      {
        "type": "commonMistake",
        "text": "Wystawienie figury na zajęty już obszar. Korekta: na obszarze może stanąć figura tylko wtedy, gdy w momencie wystawienia ten obszar jest wolny od figurek."
      },
      {
        "type": "commonMistake",
        "text": "Uznanie, że dokładający ostatni kafelek zawsze dostaje punkty. Korekta: punktuje kontrolujący obszar według większości, nie autor zamykającego ruchu."
      },
      {
        "type": "heading",
        "text": "Obszary i punktowanie",
        "level": 2
      },
      {
        "type": "commonMistake",
        "text": "Dzielenie punktów przy remisie siły. Korekta: każdy gracz z największą siłą dostaje pełną wartość obszaru."
      },
      {
        "type": "commonMistake",
        "text": "Punktowanie pól w trakcie gry albo zdejmowanie chłopa po „ukończeniu” łąki. Korekta: pola punktują wyłącznie na końcu; chłop zwykle zostaje do finału."
      },
      {
        "type": "commonMistake",
        "text": "Ukończenie klasztoru bez sąsiadów po przekątnej. Korekta: klasztor wymaga wszystkich ośmiu sąsiadów, w tym narożnych."
      },
      {
        "type": "commonMistake",
        "text": "Liczenie nieukończonego miasta na końcu po 2 punktach za kafelek. Korekta: na końcu nieukończone miasto daje 1 za kafelek i 1 za herb."
      },
      {
        "type": "heading",
        "text": "Koniec gry i remisy",
        "level": 2
      },
      {
        "type": "commonMistake",
        "text": "Kończenie partii zaraz po dobraniu ostatniego kafelka. Korekta: ostatnia tura musi być w pełni rozegrana i rozpatrzona."
      },
      {
        "type": "commonMistake",
        "text": "Rozstrzyganie remisu zwycięstwa dodatkowym kryterium. Korekta: ten sam najwyższy wynik oznacza wspólne zwycięstwo."
      },
      {
        "type": "paragraph",
        "text": "Łączenie dwóch zajętych obszarów jest legalne: powstaje jeden obszar z figurami obu graczy, a przy punktowaniu stosuje się większość. To nie jest błąd — to świadoma decyzja o remisie albo przejęciu kontroli."
      },
      {
        "type": "crossReference",
        "sectionId": "majority-ties",
        "label": "Większość i remisy siły"
      },
      {
        "type": "crossReference",
        "sectionId": "game-end",
        "label": "Koniec gry i remis zwycięstwa"
      },
      {
        "type": "crossReference",
        "sectionId": "base-faq",
        "label": "Mikro-FAQ podstawy"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Krawędzie muszą pasować; figura tylko na wolny obszar.",
          "Punkty należą do kontrolującego, nie do zamykającego.",
          "Remis siły = pełne punkty; pola tylko na końcu.",
          "Remis wyniku końcowego = wspólne zwycięstwo."
        ]
      },
      {
        "type": "qa",
        "question": "Czy wolno połączyć dwa miasta, w których stoją figury różnych graczy?",
        "answer": "Tak. Po połączeniu powstaje jeden obszar. Przy punktowaniu porównuje się siłę figurek i stosuje się zasadę większości oraz remisu."
      }
    ]
  },
  {
    "id": "base-faq",
    "title": "FAQ gry podstawowej",
    "requiredModuleIds": [
      "base"
    ],
    "editionTag": "C3",
    "sourceIds": [
      "polish-base-c3",
      "corpus-base"
    ],
    "keywords": [
      "faq",
      "pytania",
      "podstawa"
    ],
    "level": "basic",
    "hubGroup": "Przykłady",
    "content": [
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "To zebrane odpowiedzi na pytania, które pojawiają się przy stole podczas pierwszej i drugiej partii podstawy. Pełniejszy katalog FAQ (także dodatków) jest dostępny w osobnym ekranie FAQ aplikacji; tutaj zostają pytania kluczowe dla poprawnej partii bez papierowej instrukcji."
      },
      {
        "type": "qa",
        "question": "Czy muszę wystawić figurę po dołożeniu kafelka?",
        "answer": "Nie. Wystawienie figury jest zawsze opcjonalne. Możesz zachować meepla na później, zwłaszcza gdy wszystkie dostępne obszary na nowym kafelku są już zajęte albo niepewne."
      },
      {
        "type": "qa",
        "question": "Czy mogę wystawić figurę na starszym kafelku, dołożonym wcześniej?",
        "answer": "W grze podstawowej nie. Figura może stanąć wyłącznie na obszarze należącym do kafelka właśnie dołożonego w tej turze. Wyjątki pojawiają się dopiero w aktywnych dodatkach, na przykład przy magicznym portalu."
      },
      {
        "type": "qa",
        "question": "Czy wolno wejść do zajętego miasta „od drugiej strony”?",
        "answer": "Nie bezpośrednio. Możesz wystawić rycerza tylko w mieście, które w chwili wystawienia jest wolne. Później wolno połączyć dwa miasta kafelkiem — wtedy figury spotykają się na jednym obszarze i decyduje większość."
      },
      {
        "type": "qa",
        "question": "Czy przy remisie siły dzielimy punkty obszaru?",
        "answer": "Nie. Każdy gracz z największą siłą na ukończonym obszarze otrzymuje pełną wartość punktową tego obszaru. Punktów nie dzieli się na pół."
      },
      {
        "type": "qa",
        "question": "Czy klasztor wymaga kafelków po przekątnej?",
        "answer": "Tak. Klasztor jest ukończony dopiero wtedy, gdy ma wszystkich ośmiu sąsiadów: bokami i narożnikami. Dopiero wtedy punktuje 9 punktów w trakcie gry."
      },
      {
        "type": "qa",
        "question": "Czy pole może zostać ukończone i zapunktowane w trakcie partii?",
        "answer": "Nie. Pola nigdy nie kończą się w trakcie gry. Chłop pozostaje zwykle do punktowania końcowego i wtedy otrzymuje 3 punkty za każde ukończone miasto stykające się z jego polem."
      },
      {
        "type": "qa",
        "question": "Co jeśli po finale dwóch graczy ma ten sam najwyższy wynik?",
        "answer": "Dzielą zwycięstwo. Nie rozstrzyga się remisu liczbą miast, herbów ani wolnych figurek — porównuje się wyłącznie końcowy wynik na torze."
      },
      {
        "type": "crossReference",
        "sectionId": "common-patterns",
        "label": "Najczęstsze błędy podstawy"
      },
      {
        "type": "crossReference",
        "sectionId": "turn-structure",
        "label": "Pełna kolejność tury"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Figura jest opcjonalna i zwykle tylko na nowym kafelku.",
          "Zajętego obszaru nie zajmujesz wprost — możesz połączyć później.",
          "Remis siły = pełne punkty; remis wyniku = wspólne zwycięstwo.",
          "Pola i chłopi punktują wyłącznie na końcu."
        ]
      }
    ]
  }
];
